import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { DirectoryNode, FileNode, FileSystemTree } from '@webcontainer/api'
import { nanoid } from 'nanoid'
import {
  CDN_URL,
  PDF_FILE_THUMBNAIL_URL,
  PRD_FILE_NAME,
  SCRATCHPAD_FILE_NAME,
  SUGGESTIONS_FILE_NAME,
  STATUS_FILE_NAME,
} from './constant'
import type {
  User,
  Project,
  ProjectSubscriptionPlan,
  TreeNodeItem,
  WorkspaceSubscriptionPlan,
} from './types'
import type { ColumnDef } from '@tanstack/react-table'
import { getFileSystemProjectConfig } from './file-system-tree'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function isProtectedPath(
  path: string,
  protectedPaths: string[],
): boolean {
  return protectedPaths.some((protectedPath) => path.startsWith(protectedPath))
}

export function removeLocaleFromPath(path: string, locales: string[]): string {
  const localePattern = new RegExp(`^/(${locales.join('|')})`)
  return path.replace(localePattern, '')
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Generate a unique request ID for tracking and debugging
 */
export const generateRequestId = () => nanoid(10)

/**
 * Create a fetch configuration with requestId automatically injected into headers
 * @param config Original fetch configuration
 * @returns Fetch configuration with requestId in headers
 */
export const createFetchConfigWithRequestId = (
  config: RequestInit = {},
): RequestInit => {
  const requestId = generateRequestId()
  return {
    ...config,
    headers: {
      ...((config.headers as Record<string, string>) || {}),
      'X-Request-ID': requestId,
    },
  }
}

/**
 * Overrides the global fetch method to automatically inject requestId
 * Call this function once at the application entry point
 */
export const setupRequestIdInjection = () => {
  // Only run in browser environment
  if (typeof window === 'undefined') {
    return
  }

  // Prevent multiple initializations
  if ((window as any).__requestIdInjectionEnabled) {
    console.log('RequestId injection already enabled')
    return
  }

  // Store the original fetch function
  const originalFetch = window.fetch

  // Override with our custom implementation
  window.fetch = ((...args) => {
    // First argument is the URL
    const url = args[0]
    // Second argument is the config (optional)
    const config = args[1] || {}

    // Create config with requestId
    const configWithRequestId = createFetchConfigWithRequestId(config)

    // Call the original fetch with our modified config
    return originalFetch.call(window, url, configWithRequestId)
  }) as typeof fetch

  // Mark as initialized
  ;(window as any).__requestIdInjectionEnabled = true

  console.log('RequestId injection enabled for all fetch calls')
}

class FetchError extends Error {
  info: any
  status: number

  constructor(message: string, status: number, info: any) {
    super(message) // Call the parent constructor with the message.
    this.name = 'FetchError' // Custom name for the error class.
    this.info = info // Attach extra info about the error.
    this.status = status // Attach the HTTP status code.
  }
}

export const fetcher = async (...args: Parameters<typeof fetch>) => {
  // Generate request ID
  const requestId = generateRequestId()
  const projectId = new URL(window.location.href).searchParams.get('id') || ''
  // Get parameters
  let [url, config = {}] = args

  // Ensure config.headers exists
  config = {
    ...config,
    headers: {
      ...((config as RequestInit).headers || {}),
      'X-Request-ID': requestId, // Inject requestId to header
      'x-project-id': projectId,
    },
  }

  const res = await fetch(url, config as RequestInit)
  // If the status code is not in the range 200-299,
  // we still try to parse and throw it.
  if (!res.ok) {
    const errorInfo = await res.json()
    const defaultErrorMessage = 'An error occurred while fetching the data.'
    const errorMessage = errorInfo?.error || defaultErrorMessage
    // if (errorMessage) toast.error(errorMessage)
    throw new FetchError(errorMessage, res.status, errorInfo)
  }
  return res.json()
}

export function isObjectId(id: string) {
  // check if id is a 24 character hex string, 12 byte Uint8Array, or an integer
  return /^[0-9a-fA-F]{24}$/.test(id)
}

export function getUsernameFromEmail(email: string) {
  return email.split('@')[0]
}

export function getRandomNameFromEmail(email: string) {
  return email.split('@')[0].slice(0, 6) + Math.floor(Math.random() * 900 + 100)
}

export async function uploadBlobToCdn(
  body: Blob,
  filename: string,
  contentType: string,
  signal?: AbortSignal,
): Promise<string> {
  const resp = await fetch('/api/upload/pre-signed', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      filename,
      contentType,
    }),
    signal,
  })
  const data = await resp.json()
  await fetch(data.url, {
    method: 'PUT',
    body,
    signal,
  })
  return `${CDN_URL}/${filename}`
}

export function generateCdnImageUrl(
  url: string,
  options?: { width?: number; height?: number; quality?: number },
) {
  if (url.toLowerCase().endsWith('.pdf')) {
    return PDF_FILE_THUMBNAIL_URL
  }
  if (!url.startsWith(CDN_URL)) {
    return url
  }
  const path = url.replace(CDN_URL, '')
  return `${CDN_URL}/cdn-cgi/image/width=${options?.width || 200}${
    options?.height ? `,height=${options.height}` : ''
  },fit=cover,quality=${options?.quality || 80}${path}`
}

export function generateShortId(objectId: string) {
  return objectId.slice(-8)
}

/**
 * Format user credits/coins with up to two decimals.
 * - Keeps integers without trailing decimals (e.g., 30)
 * - Rounds to at most 2 decimals (e.g., 6278.2717379999995 -> 6278.27)
 * - Avoids grouping separators for consistency in the dashboard
 */
export function formatCredits(value?: number | null): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '0'
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: false,
  })
  return formatter.format(value)
}

/**
 * 避免 window.open 被浏览器拦截的安全新窗口打开方法
 * @param url 要打开的链接
 * @param id 临时元素的唯一标识符
 */
export function newWin(url: string, id: string) {
  // 先检查并移除已存在的元素
  const existingElement = document.getElementById(id)
  if (existingElement) {
    existingElement.remove()
  }

  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.setAttribute('target', '_blank')
  a.setAttribute('rel', 'noopener noreferrer')
  a.setAttribute('id', id)

  document.body.appendChild(a)
  a.click()

  // 使用 setTimeout 确保点击事件已经触发后再移除
  setTimeout(() => {
    a.remove()
  }, 100)
}

export function filterSensitiveFiles(
  fileSystemTree: FileSystemTree,
): FileSystemTree {
  const sensitivePatterns = [
    /\.env/,
    new RegExp(`^${SCRATCHPAD_FILE_NAME}$`),
    new RegExp(`^${PRD_FILE_NAME}$`),
    new RegExp(`^${SUGGESTIONS_FILE_NAME}$`),
    new RegExp(`^${STATUS_FILE_NAME}$`),
  ]
  const newFileSystemTree: FileSystemTree = {}

  for (const [name, content] of Object.entries(fileSystemTree)) {
    // Skip if the file name matches any sensitive pattern
    const isSensitive = sensitivePatterns.some((pattern) => pattern.test(name))
    if (isSensitive) {
      continue
    }

    const nodeContent = content as DirectoryNode | FileNode
    if ('directory' in nodeContent) {
      // Recursively filter directory contents
      newFileSystemTree[name] = {
        directory: filterSensitiveFiles(nodeContent.directory),
      }
    } else {
      // Keep non-sensitive files
      newFileSystemTree[name] = content
    }
  }

  return newFileSystemTree
}

/**
 * 获取域名的主机记录值
 * 对于根域名返回 '@'，对于子域名返回子域名部分
 * 例如：
 * - example.com -> '@'
 * - sub.example.com -> 'sub'
 * - deep.sub.example.com -> 'deep.sub'
 */
export function getDomainHostRecord(domainName: string): string {
  const parts = domainName.split('.')
  if (parts.length > 2) {
    return parts.slice(0, -2).join('.')
  }
  return '@'
}

export function getDomainRootHost(domainName: string): string {
  const parts = domainName.split('.')
  if (parts.length > 2) {
    return parts.slice(-2).join('.')
  }
  return domainName
}

export function generateVerificationCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString()
}

export function truncateString(str: string, maxLength: number): string {
  if (str.length > maxLength) {
    let truncated = str.slice(0, maxLength).trim()
    truncated = truncated.replace(
      /^[\s.,\/#!$%\^&\*;:{}=\-_`~()。！？、，；：""''（），《》]+|[\s.,\/#!$%\^&\*;:{}=\-_`~()。！？、，；：""''（），《》]+$/g,
      '',
    )
    return `${truncated}...`
  }
  return str
}

export function isValidDomain(domain: string): {
  isValid: boolean
  error?: string
} {
  try {
    // Convert IDN to ASCII if needed
    const asciiDomain = domain.startsWith('xn--')
      ? domain
      : new URL(`http://${domain}`).hostname

    // Check total length
    if (asciiDomain.length > 253) {
      return {
        isValid: false,
        error: 'Domain name is too long (max 253 characters)',
      }
    }

    // Split into labels
    const labels = asciiDomain.split('.')
    if (labels.length < 2) {
      return { isValid: false, error: 'Domain must have at least two parts' }
    }

    // Check domain depth (limit to 4 levels: e.g., sub.example.com)
    if (labels.length > 4) {
      return {
        isValid: false,
        error: 'Too many subdomain levels (max 4 levels allowed)',
      }
    }

    const labelRegex = /^(?!-)[a-zA-Z0-9-]{1,63}(?<!-)$/

    // Validate TLD (last part)
    const tld = labels[labels.length - 1].toLowerCase()
    if (tld.length < 2 || tld.length > 63) {
      // Changed to standard label length limit
      return { isValid: false, error: 'Invalid top-level domain length' }
    }

    // Validate all parts including TLD
    for (const label of labels) {
      if (!labelRegex.test(label)) {
        return {
          isValid: false,
          error:
            'Domain parts must be 1-63 characters long and contain only letters, numbers, and hyphens',
        }
      }
      if (/^\d+$/.test(label)) {
        return { isValid: false, error: 'Domain parts cannot be all numbers' }
      }
    }

    return { isValid: true }
  } catch (error) {
    return { isValid: false, error: 'Invalid domain format' }
  }
}

export function convertFileSystemTreeToTreeViewData(
  fileSystemTree: FileSystemTree,
  strict = true,
): TreeNodeItem[] {
  const SECRET_FILES = [
    PRD_FILE_NAME,
    SCRATCHPAD_FILE_NAME,
    SUGGESTIONS_FILE_NAME,
  ]

  const tree = Object.entries(fileSystemTree).reduce(
    (tree: TreeNodeItem[], [name, node]) => {
      if ('file' in node) {
        if (strict && SECRET_FILES.includes(name)) {
          return tree
        }
        tree.push({
          id: crypto.randomUUID(),
          name,
          type: 'file',
          contents: (node.file as { contents: string }).contents,
        })
      } else if ('directory' in node) {
        tree.push({
          id: crypto.randomUUID(),
          name,
          type: 'directory',
          children: convertFileSystemTreeToTreeViewData(node.directory),
        })
      }
      return tree
    },
    [],
  )

  tree.sort((a, b) => {
    if (a.name === 'src') return -1
    if (b.name === 'src') return 1
    if (a.type === 'file' && b.type === 'directory') return 1
    if (a.type === 'directory' && b.type === 'file') return -1
    return 0
  })

  return tree
}

export function mapKeyToObject<T, K extends keyof T>(
  list: T[],
  key: K,
): Record<(T[K] & string) | number | symbol, T> {
  return list.reduce(
    (acc, item) => {
      const keyValue = item[key]
      if (
        typeof keyValue === 'string' ||
        typeof keyValue === 'number' ||
        typeof keyValue === 'symbol'
      ) {
        acc[keyValue] = item
      }
      return acc
    },
    {} as Record<(T[K] & string) | number | symbol, T>,
  )
}

export async function sha256(data: string) {
  const hash = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(data),
  )
  return toHex(hash)
}

export function toHex(buffer: ArrayBuffer) {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

export function joinPath(from = '', to = '') {
  const fromParts = from.split('/')
  fromParts.pop() // remove file name
  const toParts = to.split('/')

  for (const part of toParts) {
    if (part === '..') {
      fromParts.pop()
    } else if (part !== '.') {
      fromParts.push(part)
    }
  }

  return fromParts.join('/')
}

export function extractUrls(text: string, maxUrls: number): string[] {
  const urlRegex =
    /(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?::\d+)?(?:\/[^\s()<>［］{}|\\^"']*(?:\([^\s()<>]*\)|[^\s`!()\[\]{};:'".,<>?«»""''］［])*)?/g
  const matches = text.match(urlRegex) || []
  // console.log('extractUrls', text, matches)
  return matches.slice(0, maxUrls)
}

export async function captureScreenshot(url: string): Promise<string> {
  const fullUrl =
    !url.startsWith('https://') && !url.startsWith('http://')
      ? `https://${url}`
      : url

  const filename = `screenshots/${nanoid(8)}.jpg`
  const response = await fetch('/api/screenshot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: fullUrl,
      filename,
      fullscreen: true,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Screenshot failed')
  }

  const data = await response.json()
  return data.url
}

export function convertToCSV(
  data: any[],
  columns: (
    | ColumnDef<any>
    | { name: string; type: string; nullable: boolean }
    | { name: string; type: string }
  )[],
) {
  if (!data || !data.length) return ''

  // Create header row
  const header = columns
    .map((col) => {
      // Handle @tanstack/react-table column definitions
      if ('accessorKey' in col) {
        return col.accessorKey as string
      }
      // Handle both ProjectTable columns and users columns
      return (col as { name: string }).name
    })
    .join(',')

  // Create data rows
  const rows = data.map((item) =>
    columns
      .map((col) => {
        const key =
          'accessorKey' in col
            ? (col.accessorKey as string)
            : (col as { name: string }).name
        const value = item[key]
        // Handle special cases (null, undefined, strings with commas or newlines)
        if (value === null || value === undefined) return ''
        if (typeof value === 'string') {
          // If the string contains commas, newlines, or quotes, wrap it in quotes
          // and escape any existing quotes by doubling them
          if (
            value.includes(',') ||
            value.includes('\n') ||
            value.includes('"')
          ) {
            return `"${value.replace(/"/g, '""')}"`
          }
        }
        return value
      })
      .join(','),
  )

  return [header, ...rows].join('\n')
}

export function downloadAsCSV(csvContent: string, fileName: string) {
  // Add UTF-8 BOM (Byte Order Mark) to support Chinese characters
  const BOM = '\uFEFF'
  const csvContentWithBOM = BOM + csvContent

  const blob = new Blob([csvContentWithBOM], {
    type: 'text/csv;charset=utf-8;',
  })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Function to get all files from a FileSystemTree
export function getAllFiles(
  tree: FileSystemTree,
  basePath = '',
): { path: string; content: string }[] {
  const files: { path: string; content: string }[] = []

  for (const [name, node] of Object.entries(tree)) {
    const currentPath = basePath ? `${basePath}/${name}` : name

    if ('file' in node) {
      // It's a file node
      const fileNode = node as FileNode
      if (typeof fileNode.file === 'string') {
        files.push({ path: currentPath, content: fileNode.file })
      } else if (fileNode.file && 'contents' in fileNode.file) {
        const contents = fileNode.file.contents
        if (typeof contents === 'string') {
          files.push({ path: currentPath, content: contents })
        } else if (contents instanceof Uint8Array) {
          // Convert Uint8Array to string using TextDecoder
          const decoder = new TextDecoder()
          files.push({ path: currentPath, content: decoder.decode(contents) })
        }
      }
    } else if ('directory' in node) {
      // It's a directory node, recursively get files
      const directoryNode = node as DirectoryNode
      files.push(...getAllFiles(directoryNode.directory, currentPath))
    }
  }

  return files
}

export function checkStripeAccountEnabled(user: User) {
  if (!user.charges_enabled || !user.payouts_enabled) {
    return false
  }
  if ((user.requirements?.currentlyDue?.length ?? 0) > 0) {
    return false
  }

  return true
}

export function isUserSubscribed(user: User) {
  return user.account_type === 'premium'
}

// 移除路径前缀
export const removePrefix = (path: string, prefix: string) => {
  if (!path) return path
  if (path.startsWith(prefix)) {
    return path.slice(prefix.length)
  }
  return path
}

// 移除路径后缀
export const removeAfterfix = (path: string, afterfix: string) => {
  if (!path) return path
  return path.replace(new RegExp(`${afterfix}$`), '')
}

/**
 * 同步版本的canProjectUseFeature，用于向后兼容
 * 只能检查project.project_subscription，已废弃
 *
 */
export function canProjectUseFeatureSync(
  project?: Project,
  planNames: ProjectSubscriptionPlan | ProjectSubscriptionPlan[] = 'basic',
): boolean {
  if (!project || !project.project_subscription) return false
  const { status, plan } = project.project_subscription
  const plans = Array.isArray(planNames) ? planNames : [planNames]
  return (status === 'active' || status === 'trialing') && plans.includes(plan)
}

export function getRewardfulCouponId(): string {
  if (typeof window !== 'undefined') {
    return (window as any)?.Rewardful?.coupon?.id || ''
  }
  return ''
}

export function getRewardfulRefId(): string {
  if (typeof window !== 'undefined') {
    return (window as any)?.Rewardful?.referral || ''
  }
  return ''
}

export function isUserPremium(user: User) {
  return user?.account_type === 'premium'
}

export function getProjectPlan(project: Project) {
  if (!project?.project_subscription) return 'free'
  if (project?.project_subscription?.status !== 'active') return 'free'
  return project?.project_subscription?.plan
}

export function isUserOrProjectPremium(user: User, project: Project) {
  if (isUserPremium(user)) return true
  if (getProjectPlan(project) === 'premium') return true
  return false
}

/**
 * 格式化标题为URL：全小写，空格用"-"代替，移除特殊字符
 * 例如: 'Some Web App' -> 'some-web-app'
 */
export function formatUrl(title: string): string {
  return (
    title
      .toLowerCase()
      .trim()
      // 将空格、连字符、下划线替换为单个连字符
      .replace(/[\s\-_]+/g, '-')
      // 移除特殊字符，保留字母、数字和连字符
      .replace(/[^a-z0-9\-]/g, '')
      // 移除开头和结尾的连字符
      .replace(/^-+|-+$/g, '')
      // 将多个连续的连字符替换为单个连字符
      .replace(/-+/g, '-')
  )
}

/**
 * 反格式化标题为URL
 * 例如: 'some-web-app' -> 'Some Web App'
 */
export function unFormatUrl(title: string): string {
  if (!title) return title

  return title
    .split('-')
    .filter((word) => word.length > 0) // 过滤掉空字符串（处理连续连字符的情况）
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // 每个单词首字母大写
    .join(' ') // 用空格连接单词
}

/**
 * Converts a template name to a URL-friendly kebab-case format
 * @param templateName - The template name to convert
 * @returns The kebab-case version of the template name
 *
 * Examples:
 * - "Activewear Store" -> "activewear-store"
 * - "Pet Shop" -> "pet-shop"
 * - "Coffee & Tea" -> "coffee-tea"
 * - "Fitness Studio" -> "fitness-studio"
 */
export function toTemplateId(templateName: string): string {
  return templateName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

export const radialGradient = (style: React.CSSProperties = {}) => {
  return {
    ...style,
    background:
      'radial-gradient(48.03% 49.3% at 50.66% 46.26%, #003C71 0%, #000106 100%)',
  }
}

const knownImageCDNDomains = [
  'images.unsplash.com',
  'res.cloudinary.com',
  'imgix.net',
  'ik.imagekit.io',
  'googleusercontent.com',
  'fbcdn.net',
  'twimg.com',
  'cdn.shopify.com',
]

export const isImageURL = (url: string) => {
  return knownImageCDNDomains.some((domain) => url.includes(domain))
}

export const isValidHTML = (str: string): boolean => {
  const trimmed = str.trim()
  if (!trimmed) return false

  // 正则匹配所有标签（包括自闭合标签）
  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)(?:\s+[^>]*?)?\/?>/g
  const stack: string[] = []
  let lastTagPos = 0
  let match: RegExpExecArray | null

  while ((match = tagRegex.exec(trimmed)) !== null) {
    const [fullTag, tagName] = match
    const isClosingTag = fullTag.startsWith('</')
    const isSelfClosing = fullTag.endsWith('/>')

    // 自闭合标签（如 <img/>）不参与栈匹配
    if (isSelfClosing) continue

    if (isClosingTag) {
      // 检查闭合标签是否匹配最近的开放标签
      if (stack.length === 0 || stack.pop() !== tagName) {
        return false
      }
    } else {
      // 开放标签入栈
      stack.push(tagName)
    }
    lastTagPos = tagRegex.lastIndex
  }

  // 检查是否所有标签都闭合
  if (stack.length > 0) return false

  // 检查是否有未正确闭合的文本（如 `<a>xxx<b></a>` 里的 `xxx<b>`）
  if (lastTagPos < trimmed.length) {
    const remainingContent = trimmed.slice(lastTagPos)
    // 如果剩余内容包含 `<` 或 `>`，说明有未闭合的标签
    if (/<[^>]*$/.test(remainingContent)) {
      return false
    }
  }

  return true
}

export function deleteUrlParam(param: string): void {
  const url = new URL(window.location.href)
  url.searchParams.delete(param)
  window.history.replaceState({}, '', url.toString())
}

export function setUrlParam(param: string, value: string): void {
  const url = new URL(window.location.href)
  url.searchParams.set(param, value)
  window.history.replaceState({}, '', url.toString())
}

export function isEcommerceProject(project: Project): boolean {
  const projectConfig = getFileSystemProjectConfig(
    project?.file_system_tree || {},
  )
  return (
    project?.template_type === 'worker' &&
    projectConfig.enable_e_commerce === true
  )
}
